import { Injectable, isDevMode, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { AngularIndexedDB } from 'angular2-indexeddb';

import { HisBrowserDetectionService } from '../base/his-browser-detection.service';
import { HisBackendService } from './his-backend.service';

/**
 * caches REST service invocations.
 */
@Injectable({providedIn: 'root'})
export class HisCacheService {
    private hisBackendService = inject(HisBackendService);
    private browserDetectionService = inject(HisBrowserDetectionService);


    // Server-side cache keys, matching their counterparts in class AngularCacheController.
    public static readonly UITEXTS_SERVER_VERSION_KEY = 'version:api/v1/cs/sys/uitexts';

    private useIndexedDB: boolean;
    private db: AngularIndexedDB;
    private dbOpenPromise: Promise<any>;
    private serverVersions: Record<string, number>;

    // "In memory" cache
    private inMemoryCache = new Map<string, Observable<any>>();

    constructor() {

        this.useIndexedDB = this.canUseIndexedDB();
        if ( this.useIndexedDB ) {
            this.db = new AngularIndexedDB( 'cache', 5 );
            this.dbOpenPromise = this.db.openDatabase( 5, ( evt: IDBVersionChangeEvent & { currentTarget: IDBOpenDBRequest } ) => {
                console.log( 'HisCacheService: create cache' );
                evt.currentTarget.result.createObjectStore( 'cache', {} );
            } );
        }
    }

    /**
     * Can we use the indexedDB to cache uitexts ?
     * There is an issue with MS IE and Edge: https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/14372843/
     */
    private canUseIndexedDB(): boolean {
        this.browserDetectionService.detectBrowser();
        let browserName = this.browserDetectionService.browserName;
        if ( browserName === 'Edge' ) {
            return false;
        }
        if ( browserName === 'IE' ) {
            let majorVersion = this.browserDetectionService.majorVersion;
            if ( majorVersion <= 9 ) {
                // IE 8, 9 have no indexedDB support at all
                return false;
            }
            let minorVersion = this.browserDetectionService.minorVersion;
            if ( majorVersion === 11 && minorVersion >= '15.' ) { // TODO wouldn't work for minor versions like "100.xxx"
                return false;
            }
        }
        // TODO further refinement by major/minor version when more data gets known

        // all other browsers should support it
        return true;
    }

    public setServerVersions( serverVersions: Record<string, number> ) {
        this.serverVersions = serverVersions;
    }

    /**
     * Retrieves data from the server or the cache, deriving the versionKey from the url parameter.
     *
     * @param url the URL of the REST service to call
     */
    public getDataCachedByIndexedDB( url: string ): Observable<any> {
        return this.getDataCachedByIndexedDB2( url, 'version:' + url, 'version:' + url );
    }

    /**
     * Retrieves data from the server or the cache, using an explicit versionKey.
     *
     * @param url the URL of the REST service to call
     * @param versionKey a special version key
     */
    public getDataCachedByIndexedDB2( url: string, clientVersionKey: string, serverVersionKey: string ): Observable<any> {
        console.log( 'his-cache-service#getDataCachedByIndexedDB2() #serverVersionKey', serverVersionKey );
        let uitextServerVersion: number = this.serverVersions[serverVersionKey];
        console.log( 'his-cache-service#getDataCachedByIndexedDB2() #uitextServerVersion', uitextServerVersion );

        // When indexedDB is ready, read the url from the database
        // If there is no appropriate object, ask the server and store it
        let subject = new BehaviorSubject( {} );
        if ( this.useIndexedDB ) {
            this.dbOpenPromise.then(
                () => {
                    this.db.getByKey( 'cache', clientVersionKey ).then( ( uitextClientVersion: number ) => {
                        console.log( 'his-cache-service#getDataCachedByIndexedDB2() #uitextClientVersion', uitextClientVersion );
                        console.log( 'his-cache-service#getDataCachedByIndexedDB2() #uitextServerVersion', uitextServerVersion );
                        if ( !uitextClientVersion || !uitextServerVersion || uitextClientVersion !== uitextServerVersion ) {
                            // cache is empty or outdated
                            console.log( 'his-cache-service#getDataCachedByIndexedDB2() #updateIndexedDb_1 for url', url );
                            console.log(
                                'his-cache-service#getDataCachedByIndexedDB2() #updateIndexedDb_1 clientVersionKey', clientVersionKey );
                            console.log(
                                'his-cache-service#getDataCachedByIndexedDB2() #updateIndexedDb_1 uitextServerVersion',
                                uitextServerVersion );
                            console.log( 'his-cache-service#getDataCachedByIndexedDB2() #updateIndexedDb_1 subject', subject );
                            this.updateIndexedDb( url, clientVersionKey, uitextServerVersion, subject );
                        } else {
                            let cacheEntry = this.db.getByKey( 'cache', url );
                            cacheEntry.then( ( object ) => {
                                if ( object ) {
                                    console.log( 'his-cache-service#getDataCachedByIndexedDB2() #return cached object for', url );
                                    console.log( 'his-cache-service#getDataCachedByIndexedDB2() #returned object', object );
                                    subject.next( object );
                                } else {
                                    console.log( 'his-cache-service#getDataCachedByIndexedDB2() #updateIndexedDb_2' );
                                    this.updateIndexedDb( url, clientVersionKey, uitextServerVersion, subject );
                                }
                            } );
                        }
                    } );
                },
                ( error ) => {
                    console.error( 'Failed to initialize indexeddb cache: ', error );
                    this.hisBackendService.getData( url ).subscribe( ( object: any ) => {
                        if ( Object.keys( object ).length !== 0 ) {
                            console.log( 'Cache error, return backendService object for', url );
                            subject.next( object );
                        }
                    } );
                } );
        } else {
            // IndexedDB is not working, always get texts from backend
            console.warn( 'IndexedDB is not properly supported by the browser; uitexts are read from the backend always...' );
            this.hisBackendService.getData( url ).subscribe( ( object: any ) => {
                if ( Object.keys( object ).length !== 0 ) {
                    console.log( 'Cache error, return backendService object for', url );
                    subject.next( object );
                }
            } );
        }

        return subject;
    }

    private updateIndexedDb( url: string, versionKey: string, uitextServerVersion: number, subject: BehaviorSubject<any> ) {
        this.hisBackendService.getData( url ).subscribe( ( object: any ) => {
            if ( object && Object.keys( object ).length !== 0 ) {
                this.db.update( 'cache', object, url );
                this.db.update( 'cache', uitextServerVersion, versionKey );
                console.log( 'Update indexedDB with backendService object for url', url );
                subject.next( object );
            }
        } );
    }

    /**
     * Retrieves data from the server or in-memory cache.
     *
     * @param url the URL of the REST service to call
     */
    public getDataCachedInMemory( url: string ): Observable<any> {
        // console.log( self.inMemoryCache.size, 'inMemoryCache =', self.inMemoryCache );
        let observable = this.inMemoryCache.get( url );
        // console.log( 'observable=', observable );
        if ( observable ) {
            console.log( 'Retrieved data for', url, 'from in-memory cache.' );
            return observable;
        }

        console.log( 'Load data for', url, 'from backend!' );
        let subject = new BehaviorSubject<any>( null );
        this.inMemoryCache.set( url, subject );
        this.hisBackendService.getData( url ).subscribe(
            ( data: any ) => {
                if ( data ) {
                    console.log( 'Finished loading data for', url, 'from backend.' );
                    subject.next( data );
                    if ( isDevMode() ) {
                        console.log( JSON.stringify( data, null, 2 ) );
                    }
                }
            } );

        return subject;
    }
}
